// src/pages/PhotoshootImages.jsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GeneratePhotoshootModal from "../components/GeneratePhotoshootModal";
import placeholderImage from "../assets/placeholder.jpg";
import { Plus, Download, Trash2, X, ExternalLink } from "lucide-react";
import Loader from "../components/Loader"; // ✅

const PAGE_SIZE = 12;

export default function PhotoshootImages() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const projectId = selectedProduct?.id || paramProjectId;

  const [photos, setPhotos] = useState([]); // [{ id, url, status }]
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);

  // infinite scroll
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1); // ✅ match ObjectRemover
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  // polling + preview close helpers
  const pollRef = useRef(null);
  const previewBackdrop = useRef(null);

  // ✅ Stripe-cycle quota + payment blocking (same gating style as RemoveBackground)
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,

    // gating flags (be permissive with naming differences across endpoints)
    blocked: false,
    payment_blocked: false,
    sub_status: null,
    is_blocked: false,

    period_start: null,
    period_end: null,
    window_source: null,
    shift_months: 0,
  });

  /* ----------------------------- Helpers ----------------------------- */

  // normalize array-ish fields safely
  const toArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      const t = val.trim();
      if (!t || t === "{}") return [];
      if (t.startsWith("[") && t.endsWith("]")) {
        try {
          const parsed = JSON.parse(t);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      if (t.startsWith("{") && t.endsWith("}")) {
        // support comma-separated in braces: "{a,b,c}"
        return t
          .slice(1, -1)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      // single url string
      return [t];
    }
    return [];
  };

  const mapPhotos = useCallback((arr) => {
    const list = toArray(arr);
    return list.map((url, i) => ({
      id: `photo-${i}`,
      url,
      status:
        !url || url === "/spinner.gif" || String(url).includes("#")
          ? "pending"
          : "done",
    }));
  }, []);

  const anyPending = useMemo(
    () =>
      photos.some(
        (p) => p.status !== "done" || !p.url || p.url === "/spinner.gif"
      ),
    [photos]
  );

  const visiblePhotos = useMemo(
    () => photos.slice(0, page * PAGE_SIZE),
    [photos, page]
  );

  // ✅ make this stable (DO NOT depend on `page`) — match ObjectRemover
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  // Cloudinary force-download helper (adds fl_attachment)
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore parse error
    }
    return url;
  }, []);

  /* --------------------------- Quota fetcher --------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/photoshoot/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  /* --------------------------- Data fetching --------------------------- */

  // 'silent' prevents toggling the loader during polling refreshes
  const fetchProjectData = useCallback(
    async (silent = false) => {
      // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
      if (!projectId) {
        setProductImages([]);
        setPhotos((prev) => prev.filter((p) => String(p.id).startsWith("temp-"))); // keep optimistic only
        setError(null);
        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) setInitialLoading(true);
      try {
        const token = await getToken();
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // inputs for modal
        setProductImages(toArray(res.data.images));

        // generated photos (legacy project.photoshoot_images array)
        const mapped = mapPhotos(res.data.photoshoot_images || []);
        setPhotos((prev) => {
          // keep any local temps that aren't in server list
          const temps = prev.filter((p) => String(p.id).startsWith("temp-"));
          const serverByUrl = new Set(mapped.map((m) => m.url));
          const keptTemps = temps.filter((t) => !serverByUrl.has(t.url));
          const merged = [...keptTemps, ...mapped];

          setPage(1);
          pageRef.current = 1; // ✅ match ObjectRemover
          recalcHasMore(merged, 1);

          return merged;
        });
        setError(null);
      } catch (e) {
        setError(
          e?.response?.status === 401
            ? "Session expired. Please sign in again."
            : "Failed to fetch project data"
        );
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [getToken, projectId, mapPhotos, recalcHasMore]
  );

  // Keep pageRef in sync (so merges can compute hasMore without causing refetch loops)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/photos change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < photos.length);
  }, [page, photos.length]);

  useEffect(() => {
    void fetchProjectData(false);
    void fetchQuota();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchProjectData, fetchQuota]);

  // keep route in sync with header product dropdown
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/photoshoot/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  /* ------------------------------ Polling ------------------------------ */

  // Start/refresh polling if there are any pending items.
  const beginPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;

    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        await fetchProjectData(true); // silent refresh (no Loader flicker)

        // Stop if no pending left or after a cap
        if (!anyPending || attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          void fetchQuota();
        }
      } catch {
        if (attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          void fetchQuota();
        }
      }
    }, 3000);
  }, [anyPending, fetchProjectData, fetchQuota]);

  // auto-start polling if we navigate in with pending placeholders
  useEffect(() => {
    if (anyPending && projectId) beginPolling();
  }, [anyPending, beginPolling, projectId]);

  /* ------------------------------ CTA gating ------------------------------ */
  const paymentBlocked = Boolean(quota.blocked || quota.payment_blocked);
  const isBlocked = Boolean(quota.is_blocked);

  const canGenerate =
    !isBlocked &&
    !paymentBlocked &&
    (quota.unlimited || (quota.remaining || 0) > 0);

  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  /* ------------------------------ Actions ------------------------------ */

  const handleGenerate = useCallback(
    async (imageUrl, size) => {
      setError(null);

      // ✅ No analyzed product yet → go to Analyze
      if (!projectId) {
        setError(
          "Please analyze a product first so we can save your generated assets."
        );
        navigate("/analyze");
        return;
      }

      // ✅ Payment/subscription gating
      if (!quota.unlimited && (quota.blocked || quota.payment_blocked)) {
        navigate("/pricing");
        return;
      }

      // ✅ Quota gating
      if (!quota.unlimited && (quota.remaining || 0) <= 0) {
        navigate("/pricing");
        return;
      }

      try {
        const token = await getToken();

        // optimistic spinner card
        const tempId = `temp-${Date.now()}`;
        const tempUrl = "/spinner.gif";
        setPhotos((prev) => {
          const next = [{ id: tempId, url: tempUrl, status: "pending" }, ...prev];
          recalcHasMore(next, pageRef.current);
          return next;
        });

        beginPolling();

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/photoshoot/generate`,
          { imageUrl, size, projectId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // optimistic quota decrement (pending counts as used)
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: Math.max(0, (q.remaining || 0) - 1),
            used: (q.used || 0) + 1,
          }));
        }
      } catch (e) {
        setError(e?.response?.data?.error || "Failed to generate image.");
        // remove local temps on failure
        setPhotos((prev) => {
          const next = prev.filter((p) => !String(p.id).startsWith("temp-"));
          recalcHasMore(next, pageRef.current);
          return next;
        });
        void fetchQuota();
      }
    },
    [getToken, projectId, beginPolling, navigate, quota, fetchQuota, recalcHasMore]
  );

  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  const handleDelete = useCallback(
    async (url) => {
      // If it's a temp card, just remove locally without confirm
      const isTemp = photos.some(
        (p) => p.url === url && String(p.id).startsWith("temp-")
      );
      if (isTemp) {
        setPhotos((prev) => {
          const next = prev.filter((p) => p.url !== url);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;
      try {
        const token = await getToken();
        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/photoshoot/delete`, {
          data: { imageUrl: url, projectId },
          headers: { Authorization: `Bearer ${token}` },
        });

        setPhotos((prev) => {
          const next = prev.filter((img) => img.url !== url);
          if (preview?.url === url) setPreview(null);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // quota does NOT decrease if you soft-delete on backend; refresh is ok
        void fetchQuota();
      } catch {
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, projectId, preview, photos, recalcHasMore, fetchQuota]
  );

  /* --------------------------- Infinite Scroll --------------------------- */

  // Infinite scroll (client-side over fetched list)
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  // Preview close on Esc / click-outside
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current)
        setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  // ✅ Don’t block the page with a loader when there’s no analyzed product yet
  const isInitialLoading =
    (initialLoading || quota.loading) && !!projectId && !error && photos.length === 0;

  /* ------------------------------ Render ------------------------------ */

  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 transition-all duration-300 hover:text-pink-500 hover:drop-shadow-[0_0_10px_rgba(255,0,128,0.5)]">
              Photoshoot Images
            </h1>
            <p className="text-white/70">
              Create stunning studio-quality product images using AI.
            </p>
          </div>

          {/* (Optional) quota pill + bar, same pattern as other image pages */}
          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : paymentBlocked
                  ? "border-red-500/40 bg-red-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : paymentBlocked
                  ? "Subscription inactive"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : paymentBlocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  Subscription inactive
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">{totalImages}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-6 mt-2">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages}</span>
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-3 mb-6">{error}</div>}

        {isInitialLoading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {/* ✅ CTA always shown, same click-redirect logic as RemoveBackground */}
              <div
                onClick={() => {
                  if (!projectId) {
                    navigate("/analyze");
                    return;
                  }
                  if (!canGenerate) {
                    navigate("/pricing");
                    return;
                  }
                  setModalOpen(true);
                }}
                className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg h-[300px] min-h-[160px] ${
                  !projectId || !canGenerate
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
                } transition text-center p-6`}
                title={
                  !projectId
                    ? "Analyze a product first to start generating"
                    : !canGenerate
                    ? "Out of images. Click to manage plan."
                    : "Click here to generate with AI"
                }
              >
                <Plus className="w-8 h-8 mb-2" />
                <p className="font-semibold text-base">Create Photoshoot Image</p>
                <p className="text-sm text-gray-400 mt-1">
                  {!projectId
                    ? "Analyze your first product to get started"
                    : quota.unlimited
                    ? "Unlimited images"
                    : paymentBlocked
                    ? "Payment required to continue"
                    : `You have ${remaining} left of ${totalImages} this cycle`}
                </p>
              </div>

              {/* Thumbnails */}
              {visiblePhotos.map(({ id, url, status }) => {
                const pending = status !== "done" || !url || url === "/spinner.gif";
                const displayUrl = pending ? "/spinner.gif" : url;

                return (
                  <div
                    key={id}
                    className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-3 flex items-center justify-center transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px]"
                    style={{ overflow: "hidden" }}
                  >
                    {/* Top-right delete / remove */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pending && String(id).startsWith("temp-")) {
                          // remove local temp spinner
                          setPhotos((prev) => {
                            const next = prev.filter((p) => p.id !== id);
                            recalcHasMore(next, pageRef.current);
                            return next;
                          });
                        } else {
                          handleDelete(url);
                        }
                      }}
                      className="absolute top-2 right-2 bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition z-20"
                      title={pending && String(id).startsWith("temp-") ? "Remove" : "Delete"}
                    >
                      <Trash2 size={18} />
                    </button>

                    {pending ? (
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                    ) : (
                      <>
                        <img
                          src={displayUrl}
                          alt="Photoshoot"
                          className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-transform duration-300 group-hover:opacity-70 cursor-pointer"
                          onClick={() => setPreview({ url: displayUrl, id })}
                          onError={(e) => {
                            // eslint-disable-next-line no-param-reassign
                            e.currentTarget.src = placeholderImage;
                          }}
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />
                        {/* Overlay actions */}
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                          style={{ background: "rgba(21, 21, 43, 0.15)" }}
                        >
                          <div className="flex gap-3 pointer-events-auto">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(displayUrl);
                              }}
                              className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>
                            <a
                              href={displayUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Open in new tab"
                            >
                              <ExternalLink size={20} />
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(displayUrl);
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Infinite loader */}
            {hasMore && !initialLoading && (
              <div
                ref={loaderRef}
                className="w-full flex justify-center py-8 pointer-events-none"
              >
                {loadingMore && (
                  <img src="/spinner.gif" alt="Loading..." className="w-7 h-7 mx-auto" />
                )}
              </div>
            )}
          </>
        )}

        {/* Modal */}
        {modalOpen && (
          <GeneratePhotoshootModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
          />
        )}

        {/* Unified Preview Modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>
              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>
              <div className="flex justify-center gap-4 pb-5">
                <button
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(preview.url)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
              {error && <div className="text-red-500 mt-6 px-6">{error}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
